import React, { useState, useMemo, useEffect, useRef } from "react";
import { graphql } from "gatsby";
import { LayoutInsight as Layout } from "~/templates/LayoutInsight";
import { InsightArticles } from "~/organisms/InsightArticles";
import { InsightSidebar } from "~/organisms/InsightSidebar";
import { InsightSearchBar } from "~/molecules/InsightSearchBar";
import { breakPoints } from "~/utils/variables";
import { InsightPager } from "~/molecules/InsightPager";
import { PATH } from "~/constant/path";
import { ClientOnlyLazy } from "~/molecules/ClientOnlyLazy";

// TBT 改善: Swiper を含むため遅延読み込み（SSR では Suspense 非対応のため ClientOnlyLazy を使用）
const InsightFeaturedSliderFallback = <div style={{ minHeight: 280, width: "100%" }} aria-hidden="true" />;

type Props = {
  data: {
    insightArticles: GatsbyTypes.ContentfulInsightArticleConnection;
    featuredArticles: GatsbyTypes.ContentfulInsightArticleConnection;
    categories: GatsbyTypes.ContentfulInsightCategoryConnection;
  };
  location: Location;
  pageContext: {
    basePath: string;
    insightCategoryContentfulId?: string;
    insightCategoryName?: string;
    currentPage: number;
    limit: number;
    skip: number;
    totalPages: number;
  };
};

const InsightIndex: React.FC<Props> = ({ data, pageContext, location }) => {
  const { currentPage, totalPages, insightCategoryContentfulId, insightCategoryName } = pageContext;
  const [searchResults, setSearchResults] = useState<GatsbyTypes.ContentfulInsightArticle[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const articlesRef = useRef<HTMLElement>(null);
  const prevSearchQueryRef = useRef<string>("");
  const isInitialMountRef = useRef(true);

  // URLのクエリパラメータから検索ワードを取得
  const getQueryParam = () => {
    if (typeof window === "undefined") return "";
    const params = new URLSearchParams(location.search);
    return params.get("q") || "";
  };

  const initialQuery = getQueryParam();

  const handleSearchResultsChange = (articles: any[], searching: boolean, query?: string) => {
    setSearchResults(articles);
    setIsSearching(searching);
    if (query !== undefined) {
      setSearchQuery(query);
    }
  };

  // 検索結果が表示されたときにスクロール（PCのみ）
  useEffect(() => {
    // 初期マウント時はスクロールしない
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevSearchQueryRef.current = searchQuery || initialQuery;
      return;
    }

    // PC判定（768px以上がPC）
    const isPc = typeof window !== "undefined" && window.innerWidth >= 768;

    // スマホの場合はスクロールしない（検索バーが見えなくなるのを防ぐ）
    if (!isPc) {
      prevSearchQueryRef.current = searchQuery || initialQuery;
      return;
    }

    const scrollToH2 = () => {
      if (!articlesRef.current) return;

      // H2要素（検索結果のタイトル）を探す
      const h2Element = articlesRef.current.querySelector("h2.contentTitle");
      if (h2Element) {
        const elementTop = h2Element.getBoundingClientRect().top + window.pageYOffset;
        // より上にスクロールしてH2が見えるようにする（オフセットを大きく）
        const offset = 100; // 検索バーや余白を考慮して100px上に
        window.scrollTo({ top: elementTop - offset, behavior: "smooth" });
      } else if (articlesRef.current) {
        // H2が見つからない場合は、セクションの位置から少し上にスクロール
        const elementTop = articlesRef.current.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: elementTop - 100, behavior: "smooth" });
      }
    };

    // 検索中で、検索クエリが変更されたときにスクロール
    if (isSearching && searchQuery && searchQuery !== prevSearchQueryRef.current) {
      // DOMの更新を待つために少し長めのタイミングで実行
      setTimeout(scrollToH2, 200);
    }

    // 検索が開始されたとき（false → true）にもスクロール
    if (isSearching && !prevSearchQueryRef.current) {
      // DOMの更新を待つために少し長めのタイミングで実行
      setTimeout(scrollToH2, 200);
    }

    prevSearchQueryRef.current = searchQuery || initialQuery;
  }, [isSearching, searchQuery, initialQuery]);

  const displayArticles = useMemo(() => {
    if (isSearching) {
      return {
        ...data.insightArticles,
        edges: searchResults.map((article) => ({
          node: article,
        })) as GatsbyTypes.ContentfulInsightArticleEdge[],
        totalCount: searchResults.length,
      } as unknown as GatsbyTypes.ContentfulInsightArticleConnection;
    }
    return data.insightArticles;
  }, [isSearching, searchResults, data.insightArticles]);

  const title = insightCategoryContentfulId
    ? `${insightCategoryName}｜紹介したい会社作りを加速させるRefcome（リフカム）のメディア`
    : `紹介したい会社作りを加速させるメディア｜Refcome（リフカム）- リファラル採用支援サービス`;

  return (
    <>
      <style jsx>{`
        .hero {
          position: relative;
          margin-bottom: 0;
        }
        @media (${breakPoints.sp}) {
          .wrapper {
            width: 100%;
            display: block;
          }
          .articles {
            margin: 30px auto 0;
            padding: 0 30px;
          }
          .sidebar {
            width: 100%;
            margin-top: 50px;
          }
          .searchBarSp {
            display: block;
          }
          .searchBarPc {
            display: none;
          }
        }
        @media (${breakPoints.minPc}) {
          .wrapper {
            max-width: 1000px;
            margin: 50px auto 0;
            display: flex;
          }
          .articles {
            width: calc(100% - 300px);
            padding-right: 48px;
          }
          .sidebar {
            width: 300px;
          }
          .searchBarSp {
            display: none;
          }
          .searchBarPc {
            display: block;
          }
        }
      `}</style>
      <Layout title={title}>
        <div className="hero">
          <ClientOnlyLazy<{ articles: GatsbyTypes.ContentfulInsightArticleConnection }>
            load={() =>
              import("~/organisms/InsightFeaturedSlider").then((m) => ({
                default: m.InsightFeaturedSlider,
              }))
            }
            fallback={InsightFeaturedSliderFallback}
            articles={data.featuredArticles}
          />
        </div>
        <div className="wrapper">
          <section className="articles" ref={articlesRef}>
            <div className="searchBarSp">
              <InsightSearchBar
                onSearchResultsChange={handleSearchResultsChange}
                location={location}
                initialQuery={initialQuery}
              />
            </div>
            <InsightArticles
              articles={displayArticles}
              currentCategoryId={pageContext.insightCategoryContentfulId}
              categories={data.categories}
              isSearchMode={isSearching}
              searchQuery={searchQuery || initialQuery}
            />
            {!isSearching && (
              <InsightPager
                currentPage={currentPage}
                totalPages={totalPages}
                basePath={pageContext.basePath ?? PATH.INSIGHT.to}
              />
            )}
          </section>
          <div className="sidebar">
            <div className="searchBarPc">
              <InsightSearchBar
                onSearchResultsChange={handleSearchResultsChange}
                location={location}
                initialQuery={initialQuery}
              />
            </div>
            <InsightSidebar />
          </div>
        </div>
      </Layout>
    </>
  );
};

export default InsightIndex;

export const CaseIndexQuery = graphql`
  query InsightArticleIndex($limit: Int!, $skip: Int!, $insightCategoryContentfulId: String) {
    insightArticles: allContentfulInsightArticle(
      sort: { fields: publishedAt, order: DESC }
      limit: $limit
      skip: $skip
      filter: { category: { elemMatch: { contentful_id: { eq: $insightCategoryContentfulId } } } }
    ) {
      totalCount
      edges {
        node {
          id
          category {
            id
            contentful_id
            name
            slug
          }
          contentful_id
          cover {
            id
            contentful_id
            fluid(maxWidth: 660, quality: 90) {
              base64
              aspectRatio
              src
              srcSet
              sizes
            }
          }
          id
          path
          title
          publishedAt
          tag {
            name
            slug
          }
        }
      }
    }
    featuredArticles: allContentfulInsightArticle(
      filter: { ranking: { lte: 10 } }
      sort: { fields: ranking, order: ASC }
      limit: 10
    ) {
      edges {
        node {
          id
          category {
            id
            contentful_id
            name
            slug
          }
          contentful_id
          cover {
            id
            contentful_id
            fluid(maxWidth: 1200, quality: 90) {
              base64
              aspectRatio
              src
              srcSet
              sizes
            }
          }
          id
          path
          title
          publishedAt
          tag {
            name
            slug
          }
        }
      }
    }
    categories: allContentfulInsightCategory(sort: { fields: createdAt, order: ASC }) {
      edges {
        node {
          id
          contentful_id
          slug
          name
        }
      }
    }
  }
`;
